import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, LayoutDashboard, LogOut, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getRoleTheme } from "@/lib/roleTheme";
import RoleBadge from "@/components/RoleBadge";

// User chip: shown in navbar + dashboard top bar. Avatar + name + role badge + dropdown.
export default function UserChip({ dark = false }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;
  const t = getRoleTheme(user.role);
  const initials = (user.name || user.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="user-chip"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full border transition-colors ${
          dark ? "border-white/15 hover:border-white/30 text-white" : "border-[var(--adn-line)] hover:border-[var(--adn-navy)] text-[var(--adn-text)] bg-white"
        }`}
      >
        <span
          className="h-8 w-8 rounded-full inline-flex items-center justify-center font-display font-semibold text-xs"
          style={{ backgroundColor: t.accent, color: "#fff" }}
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            initials
          )}
        </span>
        <span className="hidden sm:flex flex-col items-start leading-tight">
          <span className="text-xs font-medium max-w-[140px] truncate">{user.name || user.email}</span>
          <span className={`text-[9px] uppercase tracking-[0.12em] font-bold`} style={{ color: t.accent }}>
            {t.label}{!user.is_approved && user.role !== "seller" && user.role !== "admin" ? " · pending" : ""}
          </span>
        </span>
        <ChevronDown size={14} className={dark ? "text-white/60" : "text-[var(--adn-text-muted)]"} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-[var(--adn-line)] shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--adn-line)]" style={{ background: t.accentSoft }}>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: t.accentText }}>{t.tagline}</div>
            <div className="font-display font-semibold mt-1 truncate">{user.name || user.email}</div>
            <div className="text-xs text-[var(--adn-text-soft)] truncate">{user.email}</div>
            <div className="mt-2"><RoleBadge size="sm" /></div>
          </div>
          <button data-testid="chip-dashboard" onClick={() => { setOpen(false); navigate("/dashboard"); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2">
            <LayoutDashboard size={14} /> Dashboard
          </button>
          <button data-testid="chip-profile" onClick={() => { setOpen(false); navigate("/profile"); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2">
            <UserIcon size={14} /> Profile
          </button>
          <button data-testid="chip-logout" onClick={logout} className="w-full text-left px-4 py-2.5 text-sm hover:bg-rose-50 text-rose-700 flex items-center gap-2 border-t border-[var(--adn-line)]">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
